import React, { useState, useEffect, useCallback } from 'react';
import './index.css';
import './i18n';
import Navigation from './components/Navigation';
import DashboardSubscriber from './components/DashboardSubscriber';
import DashboardSecurityFee from './components/DashboardSecurityFee';
import ManagementSubscriber from './components/ManagementSubscriber';
import ManagementSecurityFee from './components/ManagementSecurityFee';
import ManagementFileProcessing from './components/ManagementFileProcessing';
import ManagementAuthorization from './components/ManagementAuthorization';
import ManagementUserProfile from './components/ManagementUserProfile';
import ManagementAuditTrail from './components/ManagementAuditTrail';
import ManagementAccountStatus from './components/ManagementAccountStatus';
import ReportSubscribers from './components/ReportSubscribers';
import ReportSecurityFee from './components/ReportSecurityFee';
import ReportInvoiceByOperator from './components/ReportInvoiceByOperator';
import ReportSummary from './components/ReportSummary';
import ReportTopup from './components/ReportTopup';
import ReportCollectionCheck from './components/ReportCollectionCheck';
import Login from './components/Login';
import { authAPI } from './services/api';
import {
  loadStoredPermissions,
  savePermissions,
  clearStoredPermissions,
  canAccessDashboard,
  canAccessManagement,
  canAccessReport,
  canAccessManagementSubMenu,
  getDefaultTab,
  getDefaultManagementSubMenu,
} from './utils/permissions';

function App() {
  const [activeTab, setActiveTab] = useState('Dashboard');
  const [activeSubMenu, setActiveSubMenu] = useState('Subscriber');
  const [activeDashboardSubMenu, setActiveDashboardSubMenu] = useState('Subscriber Dashboard');
  const [activeReportSubMenu, setActiveReportSubMenu] = useState('Subscribers Report');
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('authToken'));
  const [userRole, setUserRole] = useState(localStorage.getItem('userRole') || 'user');
  const [userName, setUserName] = useState(localStorage.getItem('userName') || '');
  const [userPermissions, setUserPermissions] = useState(() => loadStoredPermissions());

  const applySession = useCallback((user) => {
    const perms = user?.permissions || [];
    setUserRole(user?.role || 'user');
    setUserName(user?.username || '');
    setUserPermissions(perms);
    savePermissions(perms);

    const defaultTab = getDefaultTab(perms);
    setActiveTab(defaultTab);
    if (defaultTab === 'Management') {
      setActiveSubMenu(getDefaultManagementSubMenu(perms));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    clearStoredPermissions();
    setUserRole('user');
    setUserName('');
    setUserPermissions([]);
    setIsAuthenticated(false);
  };

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return;
    authAPI.me()
      .then((res) => {
        const user = res?.data;
        if (user?.role) setUserRole(user.role);
        if (user?.username) setUserName(user.username);
        if (user?.permissions) {
          setUserPermissions(user.permissions);
          savePermissions(user.permissions);
        }
      })
      .catch(() => handleLogout());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- validate session once on mount
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;

    if (activeTab === 'Dashboard' && !canAccessDashboard(userPermissions)) {
      setActiveTab(getDefaultTab(userPermissions));
    } else if (activeTab === 'Management' && !canAccessManagement(userPermissions)) {
      setActiveTab(getDefaultTab(userPermissions));
    } else if (activeTab === 'Report' && !canAccessReport(userPermissions)) {
      setActiveTab(getDefaultTab(userPermissions));
    } else if (
      activeTab === 'Management' &&
      !canAccessManagementSubMenu(userPermissions, activeSubMenu)
    ) {
      setActiveSubMenu(getDefaultManagementSubMenu(userPermissions));
    }
  }, [isAuthenticated, userPermissions, activeTab, activeSubMenu]);

  if (!isAuthenticated) {
    return (
      <Login
        onSuccess={(user) => {
          applySession(user);
          setIsAuthenticated(true);
        }}
      />
    );
  }

  const showDashboard = canAccessDashboard(userPermissions);
  const showManagement = canAccessManagement(userPermissions);
  const showReport = canAccessReport(userPermissions);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        activeSubMenu={activeSubMenu}
        setActiveSubMenu={setActiveSubMenu}
        activeDashboardSubMenu={activeDashboardSubMenu}
        setActiveDashboardSubMenu={setActiveDashboardSubMenu}
        activeReportSubMenu={activeReportSubMenu}
        setActiveReportSubMenu={setActiveReportSubMenu}
        userRole={userRole}
        userName={userName}
        userPermissions={userPermissions}
        onLogout={handleLogout}
      />

      {activeTab === 'Dashboard' && showDashboard && (
        <div>
          {activeDashboardSubMenu === 'Subscriber Dashboard' && <DashboardSubscriber />}
          {activeDashboardSubMenu === 'Security Fee Dashboard' && <DashboardSecurityFee />}
        </div>
      )}

      {activeTab === 'Management' && showManagement && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeSubMenu === 'Subscriber' &&
            canAccessManagementSubMenu(userPermissions, 'Subscriber') && <ManagementSubscriber />}
          {activeSubMenu === 'Security Fee' &&
            canAccessManagementSubMenu(userPermissions, 'Security Fee') && <ManagementSecurityFee />}
          {activeSubMenu === 'File Processing Monitor' &&
            canAccessManagementSubMenu(userPermissions, 'File Processing Monitor') && (
              <ManagementFileProcessing />
            )}
          {activeSubMenu === 'Authorization' &&
            canAccessManagementSubMenu(userPermissions, 'Authorization') && <ManagementAuthorization />}
          {activeSubMenu === 'User Profiles' &&
            canAccessManagementSubMenu(userPermissions, 'User Profiles') && <ManagementUserProfile />}
          {activeSubMenu === 'Audit Trail' &&
            canAccessManagementSubMenu(userPermissions, 'Audit Trail') && <ManagementAuditTrail />}
          {activeSubMenu === 'Account Status' &&
            canAccessManagementSubMenu(userPermissions, 'Account Status') && (
              <ManagementAccountStatus userRole={userRole} userPermissions={userPermissions} />
            )}
        </main>
      )}

      {activeTab === 'Report' && showReport && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {activeReportSubMenu === 'Subscribers Report' && <ReportSubscribers />}
          {activeReportSubMenu === 'Security Fee Report' && <ReportSecurityFee />}
          {activeReportSubMenu === 'Invoice by Operator' && <ReportInvoiceByOperator />}
          {activeReportSubMenu === 'Summary Report' && <ReportSummary />}
          {activeReportSubMenu === 'Top-up Report' && <ReportTopup />}
          {activeReportSubMenu === 'Collection Check Report' && <ReportCollectionCheck />}
        </main>
      )}

      {isAuthenticated && !showDashboard && !showManagement && !showReport && (
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="text-gray-600">Your account has no page permissions assigned. Contact an administrator.</p>
        </main>
      )}
    </div>
  );
}

export default App;
